import { Plus, SearchX } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useNavigate, useSearchParams } from "react-router-dom";

import ModernPageLayout from "@/components/layout/ModernPageLayout";
import { Button } from "@/components/ui/v2/button";
import { DataTable } from "@/components/ui/v2/table/data-table";
import { TableActiveFilters } from "@/components/ui/v2/table/table-active-filters";
import { TableToolbar } from "@/components/ui/v2/table/table-toolbar";
import { ROUTES } from "@/constants/routes";
import { useClientFilters } from "@/features/clients/api/useClientFilters";
import { useClients } from "@/features/clients/api/useClients";
import { ClientsFiltersDrawer } from "@/features/clients/pages/overview/components/ClientsFiltersDrawer";
import { useAuth } from "@/lib/hooks/useAuth";
import { usePageSearchParams } from "@/lib/hooks/usePageSearchParams";
import { usePageSizeSearchParams } from "@/lib/hooks/usePageSizeSearchParams";
import { useSearchSearchParams } from "@/lib/hooks/useSearchSearchParams";
import {
  useSortSearchParams,
  useTableSortHandler,
} from "@/lib/hooks/useSortSearchParams";
import { useClientTableColumns } from "@/lib/tableColumns/useClientTableColumns";
import { clearFilterValues, removeFilterValue } from "@/lib/utils/filterParams";
import { ClientType } from "@/types/client/ClientType";

const OverviewClientPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { orderBy, descending } = useSortSearchParams();
  const { page, setPage } = usePageSearchParams();
  const { pageSize, setPageSize } = usePageSizeSearchParams();
  const {
    searchValue,
    debouncedSearchValue,
    setSearchValue,
  } = useSearchSearchParams();
  const { tenantId } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const clientType = searchParams.getAll("clientType");
  const paymentType = searchParams.getAll("paymentType");

  const {
    data: clients,
    isLoading: clientsLoading,
    isFetching: clientsFetching,
  } = useClients({
    clientType: clientType.length > 0 ? clientType : undefined,
    paymentType: paymentType.length > 0 ? paymentType : undefined,
    descending,
    orderBy,
    page,
    pageSize,
    search: debouncedSearchValue,
    tenantId,
  });

  const { data: filters } = useClientFilters({
    tenantId,
    search: debouncedSearchValue,
  });

  const { clientsTableColumns, deleteDialog } = useClientTableColumns();
  const handleSort = useTableSortHandler<ClientType>();

  const handleRowClick = (client: ClientType) => {
    navigate(`${client?.id2}`, {
      state: { clientName: client?.name },
    });
  };

  const activeFilters = {
    clientType: clientType || [],
    paymentType: paymentType || [],
  };

  const handleFilters = () => {
    setIsFiltersOpen(true);
  };

  const hasActiveFilters = Object.values(activeFilters).some(
    (filters) => filters.length > 0,
  );

  const addedFilters = Object.entries(activeFilters).flatMap(([key, values]) =>
    values.map((value) => {
      let filterLabel: string | undefined;
      if (filters) {
        if (key === "clientType" && filters.clientType) {
          const filter = filters.clientType.find((f) => f.value === value);
          filterLabel = filter?.label;
        } else if (key === "paymentType" && filters.paymentType) {
          const filter = filters.paymentType.find((f) => f.value === value);
          filterLabel = filter?.label;
        }
      }

      return {
        key,
        label: t(`common.${key}`),
        value,
        valueLabel: filterLabel ?? value.toString(),
      };
    }),
  );

  const handleRemoveFilter = (filter: {
    key: string;
    value: string | number;
  }) => {
    const newParams = removeFilterValue(
      searchParams,
      filter.key,
      filter.value.toString(),
    );

    if (newParams.get("page") !== "1") {
      newParams.set("page", "1");
    }

    setSearchParams(newParams);
  };

  const handleClearFilters = () => {
    setSearchParams(clearFilterValues(searchParams, Object.keys(activeFilters)));
  };

  return (
    <ModernPageLayout
      title={t("common.clients")}
      subtitle={t("common.clientsDescription")}
    >
      <div className="flex flex-col gap-8">
        <TableToolbar
          onSearchChange={setSearchValue}
          searchValue={searchValue}
          onFiltersClick={handleFilters}
          actions={
            <Button asChild variant="default">
              <Link to={ROUTES.new}>
                <Plus className="h-4 w-4" />
                {t("common.add")} {t("common.client")}
              </Link>
            </Button>
          }
        />

        {hasActiveFilters && (
          <TableActiveFilters
            filters={addedFilters}
            onRemoveFilter={handleRemoveFilter}
            onClearFilters={handleClearFilters}
          />
        )}

        <DataTable
          columns={clientsTableColumns}
          data={clients?.data}
          isLoading={clientsLoading || clientsFetching}
          onRowClick={handleRowClick}
          orderBy={orderBy}
          descending={descending}
          onSort={handleSort}
          page={page}
          pageSize={pageSize}
          totalRecords={clients?.totalRecords ?? 0}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          getRowId={(client) => client.id2}
          emptyState={{
            icon: SearchX,
            title: t("common.noClients", "No clients found"),
            description: t(
              "common.clientsNoDataDescription",
              "Get started by creating a client",
            ),
            action: {
              label: t("common.add") + " " + t("common.client"),
              href: ROUTES.new,
              icon: <Plus className="size-4 mr-2" />,
            },
          }}
        />
      </div>

      <ClientsFiltersDrawer
        open={isFiltersOpen}
        onOpenChange={setIsFiltersOpen}
        searchParams={searchParams}
        tenantId={tenantId}
        searchValue={debouncedSearchValue}
      />

      {deleteDialog}

      <Outlet />
    </ModernPageLayout>
  );
};

export default OverviewClientPage;
